import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import {
  Bookmark,
  CalendarOff,
  Check,
  CheckCircle2,
  Clock,
  FileText,
  Image as ImageIcon,
  Link2,
  MessageSquare,
  Mic,
  Paperclip,
  Plus,
  Send,
  Share2,
  Users,
  X,
  XCircle,
} from 'lucide-react'
import {
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { NewChatDialog } from '../components/chat/new-chat-dialog'
import { MobileBackTitle } from '../components/shell/mobile-back-title'
import {
  MobileBottomSlots,
  MobileTopCenter,
  useMobilePageTitle,
} from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import {
  type AbsenceHalf,
  type AbsenceType,
  api,
  type ChatAttachment,
  type ChatListItem,
  type ChatMessage,
  type DocumentSummary,
  type Project,
} from '../lib/api'
import { invalidateAbsenceData } from '../lib/invalidate'
import { notify } from '../lib/notify'
import { useShellStore } from '../lib/shell-store'
import { type AudioRecording, recordingFilename, useAudioRecorder } from '../lib/use-audio-recorder'
import { useIsMobile } from '../lib/use-media-query'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat',
  validateSearch: (s: Record<string, unknown>) => ({
    id: typeof s.id === 'string' ? s.id : undefined,
  }),
  component: ChatPage,
})

const MAX_ATTACHMENTS = 4

function ChatPage() {
  const { id: activeId } = Route.useSearch()
  const navigate = Route.useNavigate()
  const qc = useQueryClient()
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  // 15s polling on the chat list — picks up new chats + unread count drift
  // when a coworker writes you while you're on another conversation.
  const { data: chatsData, isLoading: chatsLoading } = useQuery({
    queryKey: ['chats'],
    queryFn: () => api.listChats(),
    enabled: !!me?.user,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  })

  const chats = chatsData?.chats ?? []
  const activeChat = activeId ? chats.find((c) => c.id === activeId) : undefined

  // Projekt-Namen/-Farben für die Badges der Projekt-Chats (Hub Paket 4).
  const { data: projectsData } = useQuery({
    queryKey: ['projects', { archived: false }],
    queryFn: () => api.listProjects({ archived: false }),
    enabled: !!me?.user && chats.some((c) => c.projectId),
  })
  const projectMap = useMemo(
    () => new Map((projectsData?.projects ?? []).map((p) => [p.id, p])),
    [projectsData],
  )

  const [newChatOpen, setNewChatOpen] = useState(false)

  // Mobile-Shell (<640): Master-Detail-Weiche über den ?id-Search-Param —
  // Liste sichtbar → Titel „Chat" + ＋; Thread offen → ←+Thread-Name oben.
  const isPhone = useIsMobile()
  useMobilePageTitle('Chat')

  if (meLoading) return null
  if (!me?.user) return <Navigate to="/login" />
  const myId = me.user.id

  return (
    // <640 füllt h-full den Page-Container (in __root auf Scroller-Höhe
    // geklemmt) exakt zwischen den Shell-Bars — kein doppelter Scroller,
    // keine Kante hinter der Bottom-Bar. sm: = Tablet-Band 640–1023 (T2,
    // Topbar gelöscht, Bottom-Bar h-14): alter md-Wert 6rem + Bar 3.5rem
    // (56px) = 9.5rem, plus Bar-Safe-Area-Inset (Standalone; Browser-Tab
    // env() = 0). lg: stellt den Desktop-Wert (6rem, vorher md:) wieder
    // her — ≥1024 byte-identisch. phone:h-full: im Handy-Querformat
    // (Breite ≥640, Shell-Bars aktiv) gewännen sonst die sm:/lg:-dvh-
    // Klemmen gegen die Shell-Scroller-Höhe.
    <section className="h-full min-h-0 sm:h-[calc(100dvh-9.5rem-var(--app-safe-bottom))] lg:h-[calc(100dvh-6rem)] phone:h-full flex flex-col animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): ＋ Neuer Chat nur ohne
          offenen Thread — am Phone sitzt der Rückweg dann oben (←), im Band
          bleibt bei offenem Thread die Minimal-Bar (der Desktop-Kopf trägt
          „Neuer Chat" dort ohnehin). */}
      {!activeChat && (
        <MobileBottomSlots>
          <BarAccentPlus ariaLabel="Neuer Chat" onClick={() => setNewChatOpen(true)} />
        </MobileBottomSlots>
      )}

      {/* Thread offen: ←+Thread-Name in der Top-Mitte; ← räumt den
          ?id-Param und fällt damit auf die Liste zurück. */}
      {activeChat && (
        <MobileTopCenter>
          <MobileBackTitle
            title={chatTitle(activeChat, myId)}
            onBack={() => navigate({ search: { id: undefined } })}
            backAriaLabel="Zurück zur Chat-Liste"
          />
        </MobileTopCenter>
      )}

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Chat"
        actions={
          <Button onClick={() => setNewChatOpen(true)} variant="primary">
            <Plus className="h-4 w-4" />
            Neuer Chat
          </Button>
        }
      />

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 min-h-0">
        {/* Chat list — Phone zeigt nur EINEN Bereich zur Zeit (JS-Gate statt
            CSS, damit das 640–767-Band byte-identisch beide Panes stapelt). */}
        {(!isPhone || !activeChat) && (
          <aside className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden flex flex-col">
            <header className="px-4 py-2.5 border-b border-border-subtle bg-bg-overlay/30">
              <span className="text-xs uppercase tracking-wider text-text-tertiary">
                Konversationen ({chats.length})
              </span>
            </header>
            {chatsLoading ? (
              <p className="px-4 py-8 text-center text-sm text-text-tertiary">Lade …</p>
            ) : chats.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-text-tertiary space-y-3">
                <MessageSquare
                  className="h-10 w-10 mx-auto text-text-tertiary/60"
                  strokeWidth={1.5}
                />
                <p>Noch keine Chats — leg den ersten an.</p>
                <Button size="sm" onClick={() => setNewChatOpen(true)}>
                  <Plus className="h-4 w-4" /> Neuer Chat
                </Button>
              </div>
            ) : (
              <ul className="flex-1 overflow-y-auto divide-y divide-border-subtle">
                {chats.map((c) => (
                  <li key={c.id}>
                    <ChatRow
                      chat={c}
                      myId={myId}
                      active={c.id === activeId}
                      project={c.projectId ? (projectMap.get(c.projectId) ?? null) : null}
                      onClick={() => navigate({ search: { id: c.id } })}
                    />
                  </li>
                ))}
              </ul>
            )}
          </aside>
        )}

        {/* Active thread — Phone: nur bei offenem Thread gemountet. */}
        {(!isPhone || !!activeChat) && (
          <main className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden flex flex-col">
            {activeChat ? (
              <ChatThread
                chat={activeChat}
                myId={myId}
                myRole={me.user.role}
                mySupervisorId={me.user.supervisorId}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-6 text-text-tertiary">
                <MessageSquare className="h-12 w-12 text-text-tertiary/60 mb-3" strokeWidth={1.5} />
                <p className="text-sm">Wähle einen Chat aus oder leg einen neuen an.</p>
              </div>
            )}
          </main>
        )}
      </div>

      {newChatOpen && (
        <NewChatDialog
          myId={myId}
          onClose={() => setNewChatOpen(false)}
          onCreated={(chatId) => {
            qc.invalidateQueries({ queryKey: ['chats'] })
            navigate({ search: { id: chatId } })
            setNewChatOpen(false)
          }}
        />
      )}
    </section>
  )
}

// ============================================================================
// Chat list row
// ============================================================================

function ChatRow({
  chat,
  myId,
  active,
  project,
  onClick,
}: {
  chat: ChatListItem
  myId: string
  active: boolean
  /** Projekt-Zuordnung (Hub Paket 4) — Badge in der Titelzeile. */
  project?: { name: string; color: string | null } | null
  onClick: () => void
}) {
  const title = chatTitle(chat, myId)
  const preview = chat.lastMessage
    ? chat.lastMessage.content ||
      (chat.lastMessage.kind === 'voice'
        ? '🎤 Sprachnachricht'
        : chat.lastMessage.kind === 'file'
          ? '📎 Datei'
          : chat.lastMessage.kind === 'link'
            ? '🔗 Link'
            : '')
    : '— noch keine Nachrichten —'
  const time = chat.lastMessage?.createdAt
    ? formatRelative(new Date(chat.lastMessage.createdAt))
    : ''
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left px-4 py-3 hover:bg-bg-overlay/40 transition-colors',
        active && 'bg-accent/5',
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={cn(
            'h-9 w-9 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold',
            chat.isGroup ? 'bg-accent/15 text-accent' : 'bg-bg-overlay text-text-secondary',
          )}
        >
          {chat.isGroup ? <Users className="h-4 w-4" /> : initials(title)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <p
              className={cn(
                'truncate text-sm text-text-primary',
                chat.unread > 0 ? 'font-semibold' : 'font-medium',
              )}
            >
              {title}
            </p>
            {project && (
              <span
                className="inline-flex items-center gap-1 min-w-0 max-w-[40%] text-[11px] text-text-tertiary shrink-0"
                title={`Projekt: ${project.name}`}
              >
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: project.color ?? '#6b6259' }}
                />
                <span className="truncate">{project.name}</span>
              </span>
            )}
            {time && (
              <span className="text-[11px] text-text-tertiary tabular-nums shrink-0">{time}</span>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <p
              className={cn(
                'truncate text-xs',
                chat.unread > 0 ? 'text-text-secondary' : 'text-text-tertiary',
              )}
            >
              {preview}
            </p>
            {chat.unread > 0 && (
              <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-accent px-1.5 py-0.5 text-2xs font-semibold leading-none text-bg-base tabular-nums">
                {chat.unread > 99 ? '99+' : chat.unread}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

// ============================================================================
// Active thread
// ============================================================================

function ChatThread({
  chat,
  myId,
  myRole,
  mySupervisorId,
}: {
  chat: ChatListItem
  myId: string
  myRole: string
  mySupervisorId: string | null
}) {
  const qc = useQueryClient()
  // 5s polling on the active thread — feels live without flooding the server
  const { data, isLoading } = useQuery({
    queryKey: ['chat', chat.id, 'messages'],
    queryFn: () => api.listChatMessages(chat.id, { limit: 100 }),
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  })
  const messages = useMemo(() => {
    // Server returns newest-first; the UI wants oldest-first scrolling.
    return [...(data?.messages ?? [])].reverse()
  }, [data])

  const memberById = useMemo(() => new Map(chat.members.map((m) => [m.id, m])), [chat.members])

  const send = useMutation({
    mutationFn: (content: string) => api.sendChatMessage(chat.id, content),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', chat.id, 'messages'] })
      qc.invalidateQueries({ queryKey: ['chats'] })
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const upload = useMutation({
    mutationFn: ({ files, content }: { files: File[]; content: string }) =>
      api.uploadChatAttachment(chat.id, files, content),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', chat.id, 'messages'] })
      qc.invalidateQueries({ queryKey: ['chats'] })
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const recorder = useAudioRecorder()
  const sendVoice = useMutation({
    mutationFn: (rec: AudioRecording) =>
      api.sendVoiceMessage(chat.id, rec.blob, rec.durationMs, recordingFilename(rec.mimeType)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', chat.id, 'messages'] })
      qc.invalidateQueries({ queryKey: ['chats'] })
    },
    onError: (err: Error) => notify.error(err.message),
  })
  useEffect(() => {
    if (recorder.error) notify.error(recorder.error)
  }, [recorder.error])

  const markRead = useMutation({
    mutationFn: () => api.markChatRead(chat.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chats'] }),
  })

  // Mark read on mount + whenever a new message arrives while looking at it
  // biome-ignore lint/correctness/useExhaustiveDependencies: stable mutate ref
  useEffect(() => {
    markRead.mutate()
  }, [chat.id, messages.length])

  // Auto-scroll to bottom whenever the messages array reference changes —
  // useMemo above yields a new array on each fetch, so this fires once per
  // poll that actually returned new data.
  const endRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (messages.length === 0) return
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages])

  // Composer state
  const [draft, setDraft] = useState('')
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [sharePanelOpen, setSharePanelOpen] = useState(false)
  const [absencePanelOpen, setAbsencePanelOpen] = useState(false)

  // Tastatur vs. Bottom-Bar (Konzept-Risiko §5.2): auf Android schrumpft
  // h-dvh mit der Tastatur — die Shell-Bar bliebe als toter 56px-Streifen
  // ZWISCHEN Composer und Tastatur sichtbar. Fokus-basiertes Ausblenden
  // (statt visualViewport-Listener) ist die kleinste saubere Lösung; Blur
  // und Unmount (Thread-Wechsel/Zurück) stellen die Bar wieder her.
  const isPhone = useIsMobile()
  const setSuppressBottomBar = useShellStore((s) => s.setSuppressBottomBar)
  useEffect(() => {
    return () => setSuppressBottomBar(false)
  }, [setSuppressBottomBar])

  function addFiles(incoming: File[]) {
    if (incoming.length === 0) return
    setPendingFiles((prev) => {
      const room = MAX_ATTACHMENTS - prev.length
      if (room <= 0) {
        notify.warning(`Maximal ${MAX_ATTACHMENTS} Dateien pro Nachricht`)
        return prev
      }
      const next = [...prev, ...incoming.slice(0, room)]
      if (incoming.length > room) {
        notify.warning(`Maximal ${MAX_ATTACHMENTS} Dateien pro Nachricht`)
      }
      return next
    })
  }

  function removeFile(idx: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    const text = draft.trim()
    if (pendingFiles.length > 0) {
      if (upload.isPending) return
      upload.mutate(
        { files: pendingFiles, content: text },
        {
          onSuccess: () => {
            setDraft('')
            setPendingFiles([])
          },
        },
      )
      return
    }
    if (!text || send.isPending) return
    send.mutate(text, { onSuccess: () => setDraft('') })
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(false)
    const files = Array.from(e.dataTransfer?.files ?? [])
    addFiles(files)
  }

  // === Supervisor / absence detection =======================================
  // Only the OTHER participant in a 1-on-1 matters. We surface the quick-
  // action when they're either the current user's supervisor or an admin.
  const otherMember = chat.isGroup ? null : chat.members.find((m) => m.id !== myId)
  const canQuickAbsence = !chat.isGroup && otherMember && otherMember.id === mySupervisorId

  const title = chatTitle(chat, myId)
  const subtitle = chat.isGroup
    ? `${chat.members.length} Mitglieder · ${chat.members.map((m) => m.name).join(', ')}`
    : chat.members.find((m) => m.id !== myId)?.email

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drag handlers wrap the whole thread; the file input in the composer is the keyboard/AT entry point.
    <div
      className="flex flex-col h-full min-h-0 relative"
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return
        setDragOver(false)
      }}
      onDrop={onDrop}
    >
      <header className="px-5 py-3 border-b border-border-subtle bg-bg-overlay/30 flex items-center gap-3">
        <div
          className={cn(
            'h-9 w-9 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold',
            chat.isGroup ? 'bg-accent/15 text-accent' : 'bg-bg-overlay text-text-secondary',
          )}
        >
          {chat.isGroup ? <Users className="h-4 w-4" /> : initials(title)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary truncate">{title}</p>
          {subtitle && <p className="text-[11px] text-text-tertiary truncate">{subtitle}</p>}
        </div>
        {canQuickAbsence && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setAbsencePanelOpen(true)}
            type="button"
          >
            <CalendarOff className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Abwesenheit beantragen</span>
            <span className="sm:hidden">Abwesenheit</span>
          </Button>
        )}
      </header>

      {dragOver && (
        <div className="absolute inset-0 z-10 m-2 rounded-xl border-2 border-dashed border-accent/60 bg-accent/10 backdrop-blur-sm flex items-center justify-center pointer-events-none">
          <p className="text-sm font-medium text-accent">
            Dateien hier ablegen — max. {MAX_ATTACHMENTS}
          </p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
        {isLoading ? (
          <p className="text-center text-sm text-text-tertiary">Lade Nachrichten …</p>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-text-tertiary py-8">
            Noch keine Nachrichten — schreib die erste.
          </p>
        ) : (
          messages.map((m, idx) => {
            const prev = messages[idx - 1]
            const sameSender = prev && prev.senderUserId === m.senderUserId
            const within2min =
              prev && new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < 120_000
            const grouped = sameSender && within2min && m.kind === prev.kind
            return (
              <MessageBubble
                key={m.id}
                message={m}
                isMe={m.senderUserId === myId}
                senderName={memberById.get(m.senderUserId)?.name ?? 'Unbekannt'}
                showSender={!grouped && chat.isGroup && m.kind !== 'system'}
                showTime={!grouped}
                chatId={chat.id}
                me={{ id: myId, role: myRole }}
                otherUserId={otherMember?.id ?? null}
              />
            )
          })
        )}
        <div ref={endRef} />
      </div>

      {pendingFiles.length > 0 && (
        <div className="px-4 pt-2 flex flex-wrap gap-1.5 border-t border-border-subtle bg-bg-overlay/30">
          {pendingFiles.map((f, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: File objects have no stable id; index disambiguates duplicate filenames in the selection batch.
              key={`${f.name}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-bg-overlay/70 border border-border-subtle px-2.5 py-1 text-[11px] text-text-secondary"
            >
              <FileText className="h-3 w-3 text-text-tertiary" />
              <span className="max-w-[160px] truncate">{f.name}</span>
              <span className="text-text-tertiary tabular-nums">{formatBytes(f.size)}</span>
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="ml-0.5 text-text-tertiary hover:text-text-primary"
                aria-label="Entfernen"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <form
        onSubmit={onSubmit}
        className="px-4 py-3 border-t border-border-subtle bg-bg-overlay/20 flex items-end gap-2"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files ?? [])
            addFiles(files)
            e.target.value = ''
          }}
        />
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="h-9 w-9 rounded-md flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-bg-overlay/60 transition-colors"
            title="Datei anhängen"
            aria-label="Datei anhängen"
            disabled={upload.isPending}
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setSharePanelOpen(true)}
            className="h-9 w-9 rounded-md flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-bg-overlay/60 transition-colors"
            title="Datei als Link teilen"
            aria-label="Datei als Link teilen"
          >
            <Link2 className="h-4 w-4" />
          </button>
          {recorder.supported && !recorder.recording && (
            <button
              type="button"
              onClick={() => recorder.start()}
              className="h-9 w-9 rounded-md flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-bg-overlay/60 transition-colors"
              title="Sprachnachricht aufnehmen"
              aria-label="Sprachnachricht aufnehmen"
              disabled={sendVoice.isPending}
            >
              <Mic className="h-4 w-4" />
            </button>
          )}
        </div>
        {recorder.recording ? (
          <div className="flex-1 flex items-center gap-3 rounded-lg border border-danger/40 bg-danger/5 px-3 py-2 min-h-[52px]">
            <span className="h-2.5 w-2.5 rounded-full bg-danger animate-pulse" />
            <span className="font-mono text-sm tabular-nums text-text-primary">
              {formatRecordingTime(recorder.elapsedMs)}
            </span>
            <span className="text-xs text-text-tertiary flex-1">Aufnahme läuft…</span>
            <button
              type="button"
              onClick={() => recorder.cancel()}
              className="h-9 w-9 rounded-md flex items-center justify-center text-text-tertiary hover:text-danger transition-colors"
              title="Aufnahme verwerfen"
              aria-label="Aufnahme verwerfen"
            >
              <X className="h-4 w-4" />
            </button>
            <Button
              type="button"
              onClick={async () => {
                const rec = await recorder.stop()
                if (rec) sendVoice.mutate(rec)
              }}
              disabled={sendVoice.isPending}
            >
              <Send className="h-4 w-4" /> Senden
            </Button>
          </div>
        ) : (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={() => {
              if (isPhone) setSuppressBottomBar(true)
            }}
            onBlur={() => setSuppressBottomBar(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                onSubmit(e as unknown as FormEvent)
              }
            }}
            rows={2}
            maxLength={4000}
            placeholder={
              pendingFiles.length > 0
                ? 'Optional: Nachricht zu den Dateien …'
                : 'Nachricht schreiben… (Enter = senden, Shift+Enter = Zeilenumbruch)'
            }
            className="flex-1 rounded-lg border border-border-default bg-bg-overlay/50 px-3 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20 resize-none"
          />
        )}
        {!recorder.recording && (
          <Button
            type="submit"
            disabled={
              (pendingFiles.length === 0 && !draft.trim()) || send.isPending || upload.isPending
            }
          >
            <Send className="h-4 w-4" /> Senden
          </Button>
        )}
      </form>

      {sharePanelOpen && (
        <ShareLinkPanel
          chatId={chat.id}
          onClose={() => setSharePanelOpen(false)}
          onShared={() => {
            qc.invalidateQueries({ queryKey: ['chat', chat.id, 'messages'] })
            qc.invalidateQueries({ queryKey: ['chats'] })
            setSharePanelOpen(false)
          }}
        />
      )}

      {absencePanelOpen && canQuickAbsence && (
        <ChatAbsencePanel
          chatId={chat.id}
          recipientName={otherMember?.name ?? ''}
          onClose={() => setAbsencePanelOpen(false)}
          onSubmitted={() => {
            qc.invalidateQueries({ queryKey: ['chat', chat.id, 'messages'] })
            qc.invalidateQueries({ queryKey: ['chats'] })
            invalidateAbsenceData(qc)
            setAbsencePanelOpen(false)
          }}
        />
      )}
    </div>
  )
}

// ============================================================================
// Message bubble
// ============================================================================

function MessageBubble({
  message,
  isMe,
  senderName,
  showSender,
  showTime,
  chatId,
  me,
  otherUserId,
}: {
  message: ChatMessage
  isMe: boolean
  senderName: string
  showSender: boolean
  showTime: boolean
  chatId: string
  me: { id: string; role: string } | null
  otherUserId: string | null
}) {
  const time = new Date(message.createdAt).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  })

  if (message.kind === 'system') {
    return (
      <div className="flex justify-center my-2">
        <div className="rounded-full bg-bg-overlay/40 px-3 py-1 text-[11px] text-text-tertiary">
          {message.content}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex', isMe ? 'justify-end' : 'justify-start')}>
      <div
        className={cn('max-w-[75%] min-w-0', isMe ? 'items-end' : 'items-start', 'flex flex-col')}
      >
        {showSender && !isMe && (
          <span className="text-[11px] text-text-tertiary mb-0.5 px-1">{senderName}</span>
        )}
        {message.kind === 'file' && <AttachmentBubble message={message} isMe={isMe} />}
        {message.kind === 'voice' && <VoiceBubble message={message} isMe={isMe} />}
        {message.kind === 'link' && <LinkBubble message={message} isMe={isMe} />}
        {message.kind === 'absence_request' && (
          <AbsenceRequestBubble
            message={message}
            isMe={isMe}
            chatId={chatId}
            me={me}
            otherUserId={otherUserId}
          />
        )}
        {message.kind === 'text' && (
          <div
            className={cn(
              'rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words',
              isMe
                ? 'bg-accent text-bg-base rounded-br-sm'
                : 'bg-bg-overlay/60 text-text-primary rounded-bl-sm',
            )}
          >
            {message.content}
          </div>
        )}
        {/* For file/link, the optional caption renders inside the bubble's
            caption row; nothing else to do here. */}
        {showTime && (
          <span className="text-2xs text-text-tertiary mt-0.5 px-1 tabular-nums">{time}</span>
        )}
      </div>
    </div>
  )
}

function formatRecordingTime(ms: number): string {
  const total = Math.floor(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

// === Sprachnachricht: <audio>-Player + asynchrones Transkript ================
// Playback-Asymmetrie Android↔iOS (webm/opus vs. mp4/AAC) wird durch das
// Transkript als immer lesbaren Fallback abgefedert.

function VoiceBubble({ message, isMe }: { message: ChatMessage; isMe: boolean }) {
  const att = (message.attachments ?? [])[0]
  if (!att) return null
  return (
    <div
      className={cn(
        'rounded-2xl border px-3 py-2.5 max-w-[320px] space-y-1.5',
        isMe
          ? 'bg-accent/10 border-accent/30 rounded-br-sm'
          : 'bg-bg-overlay/60 border-border-subtle rounded-bl-sm',
      )}
    >
      <div className="flex items-center gap-2">
        <Mic className="h-4 w-4 shrink-0 text-text-tertiary" />
        {/* biome-ignore lint/a11y/useMediaCaption: Transkript wird direkt darunter gerendert */}
        <audio
          controls
          preload="metadata"
          src={api.chatAttachmentRawUrl(att.documentId)}
          className="h-9 w-56 max-w-full"
        />
      </div>
      <div className="flex items-center gap-2 px-0.5">
        {att.durationMs != null && att.durationMs > 0 && (
          <span className="font-mono text-2xs tabular-nums text-text-tertiary">
            {formatRecordingTime(att.durationMs)}
          </span>
        )}
        {message.transcriptStatus === 'pending' && (
          <span className="text-2xs text-text-tertiary animate-pulse">Transkribiere…</span>
        )}
        {message.transcriptStatus === 'failed' && (
          <span className="text-2xs text-warning">Transkription fehlgeschlagen</span>
        )}
      </div>
      {message.transcript && (
        <p className="text-sm text-text-primary whitespace-pre-wrap break-words px-0.5">
          {message.transcript}
        </p>
      )}
      {message.content && (
        <p className="text-xs text-text-secondary whitespace-pre-wrap break-words px-0.5">
          {message.content}
        </p>
      )}
    </div>
  )
}

// === Attachment grid (WhatsApp-style 1 / 2x1 / 2x2 layout) ====================

function AttachmentBubble({ message, isMe }: { message: ChatMessage; isMe: boolean }) {
  const items = message.attachments ?? []
  const count = items.length
  return (
    <div
      className={cn(
        'rounded-2xl overflow-hidden border max-w-[320px]',
        isMe
          ? 'bg-accent/10 border-accent/30 rounded-br-sm'
          : 'bg-bg-overlay/60 border-border-subtle rounded-bl-sm',
      )}
    >
      <div
        className={cn(
          'grid gap-0.5 bg-border-subtle/30',
          count === 1 && 'grid-cols-1',
          count === 2 && 'grid-cols-2',
          count >= 3 && 'grid-cols-2',
        )}
      >
        {items.map((att, idx) => (
          <AttachmentTile
            key={`${message.id}-${att.documentId}`}
            message={message}
            attachment={att}
            attachmentIdx={idx}
            count={count}
          />
        ))}
      </div>
      {message.content && (
        <p
          className={cn(
            'px-3 py-2 text-sm whitespace-pre-wrap break-words',
            isMe ? 'text-text-primary' : 'text-text-primary',
          )}
        >
          {message.content}
        </p>
      )}
    </div>
  )
}

function AttachmentTile({
  message,
  attachment,
  attachmentIdx,
  count,
}: {
  message: ChatMessage
  attachment: ChatAttachment
  attachmentIdx: number
  count: number
}) {
  const qc = useQueryClient()
  const isImage = attachment.mimeType.startsWith('image/')
  const rawUrl = api.chatAttachmentRawUrl(attachment.documentId)
  // 1 image = full-width banner, multi = squared tile
  const tileH = count === 1 ? 'h-60' : count === 2 ? 'h-32' : 'h-28'

  const saveCopy = useMutation({
    mutationFn: () => api.saveCopyFromChatMessage(message.id, attachmentIdx),
    onSuccess: () => {
      notify.success('Kopie in deinen Dateien gespeichert')
      qc.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <div className={cn('relative group bg-bg-base', tileH)}>
      <a
        href={rawUrl}
        target="_blank"
        rel="noreferrer"
        className="block h-full w-full"
        title={attachment.filename}
      >
        {isImage ? (
          <img
            src={rawUrl}
            alt={attachment.filename}
            className="h-full w-full object-cover cursor-zoom-in"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center gap-1.5 px-3 text-text-secondary hover:bg-bg-overlay/40">
            {attachment.mimeType === 'application/pdf' ? (
              <FileText className="h-8 w-8 text-text-tertiary" strokeWidth={1.5} />
            ) : isImage ? (
              <ImageIcon className="h-8 w-8 text-text-tertiary" strokeWidth={1.5} />
            ) : (
              <FileText className="h-8 w-8 text-text-tertiary" strokeWidth={1.5} />
            )}
            <span className="text-[11px] truncate max-w-full text-center text-text-primary font-medium">
              {attachment.filename}
            </span>
            <span className="text-2xs tabular-nums text-text-tertiary">
              {formatBytes(attachment.size)}
            </span>
          </div>
        )}
      </a>
      <button
        type="button"
        onClick={() => saveCopy.mutate()}
        disabled={saveCopy.isPending}
        className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-bg-base/80 backdrop-blur-sm border border-border-subtle flex items-center justify-center text-text-secondary opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity hover:text-accent disabled:opacity-50"
        title="Als Kopie speichern"
        aria-label="Als Kopie speichern"
      >
        <Bookmark className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// === Link share bubble ========================================================

function LinkBubble({ message, isMe }: { message: ChatMessage; isMe: boolean }) {
  const att = message.attachments[0]
  if (!att) return null
  return (
    <a
      href={`/documents/${att.documentId}`}
      target="_blank"
      rel="noreferrer"
      className={cn(
        'flex items-start gap-3 rounded-2xl px-3.5 py-3 max-w-[320px] border transition-colors',
        isMe
          ? 'bg-accent/10 border-accent/30 rounded-br-sm hover:bg-accent/15'
          : 'bg-bg-overlay/60 border-border-subtle rounded-bl-sm hover:bg-bg-overlay/80',
      )}
    >
      <div className="h-10 w-10 rounded-lg bg-accent/15 text-accent flex items-center justify-center shrink-0">
        <Share2 className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-2xs uppercase tracking-wider text-accent font-semibold">Link</span>
          <span className="text-2xs text-text-tertiary">
            {isMe ? 'Du hast geteilt' : 'Mit dir geteilt'}
          </span>
        </div>
        <p className="text-sm font-medium text-text-primary truncate">{att.filename}</p>
        <p className="text-[11px] text-text-tertiary tabular-nums">{formatBytes(att.size)}</p>
        {message.content && (
          <p className="mt-1 text-xs text-text-secondary whitespace-pre-wrap break-words">
            {message.content}
          </p>
        )}
      </div>
    </a>
  )
}

// === Absence-request bubble ===================================================

const ABSENCE_TYPE_LABEL: Record<string, string> = {
  vacation: 'Urlaub',
  sick: 'Krankmeldung',
  training: 'Fortbildung',
  other: 'Abwesenheit',
}

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function AbsenceRequestBubble({
  message,
  isMe,
  chatId,
  me,
  otherUserId,
}: {
  message: ChatMessage
  isMe: boolean
  chatId: string
  me: { id: string; role: string } | null
  otherUserId: string | null
}) {
  // Resolve the absence row. Mitarbeiter sieht eigenen Antrag; Vorgesetzter/
  // Admin sieht den Antrag seines Untergebenen. Beide haben Lesezugriff via
  // listAbsences (filtered by scope on the server).
  const qc = useQueryClient()
  // Direct lookup via GET /absences/:id. The endpoint authorizes based on
  // ownership + supervisor scope, so the requester and any authorized
  // reviewer both get the row; everyone else gets 403 and we render the
  // "could not load" fallback.
  const { data } = useQuery({
    queryKey: ['absence-status', message.referenceId],
    queryFn: async () => {
      try {
        const r = await api.getAbsence(message.referenceId as string)
        return r.absence
      } catch {
        return null
      }
    },
    enabled: !!message.referenceId,
    retry: false,
  })

  const absence = data ?? null
  const status = absence?.status ?? 'pending'
  const cancellationPending = !!absence?.cancellationRequested
  const StatusIcon =
    status === 'approved'
      ? CheckCircle2
      : status === 'rejected' || status === 'cancelled' || status === 'withdrawn'
        ? XCircle
        : Clock
  const statusColor =
    status === 'approved'
      ? 'text-success'
      : status === 'rejected'
        ? 'text-danger'
        : status === 'withdrawn' || status === 'cancelled'
          ? 'text-text-tertiary'
          : 'text-warning'
  const statusLabel =
    status === 'approved'
      ? 'Genehmigt'
      : status === 'rejected'
        ? 'Abgelehnt'
        : status === 'withdrawn'
          ? 'Zurückgezogen'
          : status === 'cancelled'
            ? 'Storniert'
            : 'Ausstehend'

  // canReview: I'm not the requester + I'm admin OR supervisor of the requester
  const isSupervisor =
    !isMe &&
    !!absence &&
    !!me &&
    (me.role === 'admin' || (otherUserId != null && absence.userId === otherUserId))

  // ── Mutations ───────────────────────────────────────────────────────
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['chat', chatId, 'messages'] })
    qc.invalidateQueries({ queryKey: ['absence-status', message.referenceId] })
    invalidateAbsenceData(qc)
    qc.invalidateQueries({ queryKey: ['absences', 'pending'] })
  }

  const [note, setNote] = useState('')
  const reviewMutation = useMutation({
    mutationFn: ({ s, n }: { s: 'approved' | 'rejected'; n?: string }) =>
      api.reviewAbsence(absence?.id ?? '', s, n),
    onSuccess: (_d, vars) => {
      invalidate()
      notify.success(vars.s === 'approved' ? 'Antrag genehmigt' : 'Antrag abgelehnt')
      setNote('')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const withdrawMutation = useMutation({
    mutationFn: () => api.withdrawAbsence(absence?.id ?? ''),
    onSuccess: () => {
      invalidate()
      notify.success('Antrag zurückgezogen')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const [cancelReason, setCancelReason] = useState('')
  const [cancelMode, setCancelMode] = useState(false)
  const requestCancelMutation = useMutation({
    mutationFn: (reason: string) =>
      api.requestAbsenceCancellation(absence?.id ?? '', reason || undefined),
    onSuccess: () => {
      invalidate()
      notify.success('Stornierung beantragt')
      setCancelReason('')
      setCancelMode(false)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const cancellationReviewMutation = useMutation({
    mutationFn: async ({ action, n }: { action: 'approve' | 'reject'; n?: string }) => {
      if (action === 'approve') await api.approveAbsenceCancellation(absence?.id ?? '')
      else await api.rejectAbsenceCancellation(absence?.id ?? '', n)
    },
    onSuccess: (_d, vars) => {
      invalidate()
      notify.success(vars.action === 'approve' ? 'Stornierung genehmigt' : 'Stornierung abgelehnt')
      setNote('')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // ── Body content ────────────────────────────────────────────────────
  const typeLabel = absence ? (ABSENCE_TYPE_LABEL[absence.type] ?? 'Abwesenheit') : 'Abwesenheit'
  const dateRange = absence
    ? absence.fromDate === absence.toDate
      ? fmtDay(absence.fromDate)
      : `${fmtDay(absence.fromDate)} – ${fmtDay(absence.toDate)}`
    : null
  const hasHalfDay = !!absence?.firstDayHalf || !!absence?.lastDayHalf

  return (
    <div
      className={cn(
        'rounded-2xl border max-w-[340px] overflow-hidden shadow-sm',
        isMe
          ? 'bg-accent/10 border-accent/30 rounded-br-sm'
          : 'bg-bg-overlay/60 border-border-subtle rounded-bl-sm',
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-3 px-3.5 py-3 border-b border-border-subtle">
        <div className="h-10 w-10 rounded-lg bg-warning/15 text-warning flex items-center justify-center shrink-0">
          <CalendarOff className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <span className="text-2xs uppercase tracking-wider text-warning font-semibold">
              {typeLabel} · Antrag
            </span>
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-2xs font-medium',
                statusColor,
                status === 'approved'
                  ? 'bg-success/10'
                  : status === 'rejected'
                    ? 'bg-danger/10'
                    : status === 'withdrawn' || status === 'cancelled'
                      ? 'bg-bg-overlay/40'
                      : 'bg-warning/10',
              )}
            >
              <StatusIcon className="h-3 w-3" /> {statusLabel}
            </span>
            {hasHalfDay && (
              <span className="text-2xs rounded-full bg-accent/15 border border-accent/40 text-accent px-1.5 py-0.5">
                ½
              </span>
            )}
            {cancellationPending && (
              <span className="text-2xs rounded-full bg-warning/15 border border-warning/40 text-warning px-1.5 py-0.5 uppercase tracking-wider">
                Storno?
              </span>
            )}
          </div>
          {dateRange && (
            <p className="text-sm font-medium text-text-primary tabular-nums">{dateRange}</p>
          )}
          {!dateRange && (
            <p className="text-sm text-text-primary whitespace-pre-wrap break-words">
              {message.content}
            </p>
          )}
        </div>
      </div>

      {/* Details */}
      {absence && (absence.notes || cancellationPending || absence.reviewNote) && (
        <div className="px-3.5 py-2 border-b border-border-subtle space-y-1 text-[11px] text-text-secondary bg-bg-overlay/20">
          {absence.notes && (
            <p>
              <span className="text-text-tertiary">Notiz: </span>
              {absence.notes}
            </p>
          )}
          {cancellationPending && absence.cancellationReason && (
            <p className="text-warning">
              <span>Storno-Grund: </span>
              {absence.cancellationReason}
            </p>
          )}
          {status !== 'pending' && absence.reviewNote && (
            <p>
              <span className="text-text-tertiary">
                {status === 'approved' ? 'Genehmigt' : 'Abgelehnt'} mit Notiz:{' '}
              </span>
              {absence.reviewNote}
            </p>
          )}
        </div>
      )}

      {/* ── Actions ─────────────────────────────────────────────────── */}

      {/* Supervisor: approve/reject pending request */}
      {absence &&
        isSupervisor &&
        status === 'pending' &&
        !cancellationPending &&
        !reviewMutation.isSuccess && (
          <ReviewActions
            disabled={reviewMutation.isPending}
            note={note}
            setNote={setNote}
            onApprove={() => reviewMutation.mutate({ s: 'approved', n: note.trim() || undefined })}
            onReject={() => reviewMutation.mutate({ s: 'rejected', n: note.trim() || undefined })}
          />
        )}

      {/* Supervisor: approve/reject cancellation request. Hide as soon as
         the mutation succeeds — otherwise the user can click again in the
         window between onSuccess and the refetch that flips
         cancellationPending to false. */}
      {absence && isSupervisor && cancellationPending && !cancellationReviewMutation.isSuccess && (
        <ReviewActions
          disabled={cancellationReviewMutation.isPending}
          note={note}
          setNote={setNote}
          approveLabel="Storno genehmigen"
          rejectLabel="Storno ablehnen"
          onApprove={() => cancellationReviewMutation.mutate({ action: 'approve' })}
          onReject={() =>
            cancellationReviewMutation.mutate({ action: 'reject', n: note.trim() || undefined })
          }
        />
      )}

      {/* Requester: withdraw pending request */}
      {absence && isMe && status === 'pending' && (
        <div className="px-3.5 py-2.5 border-t border-border-subtle bg-bg-overlay/20">
          <button
            type="button"
            onClick={async () => {
              const ok = await confirm({
                title: 'Antrag zurückziehen?',
                description: dateRange ?? typeLabel,
                variant: 'danger',
                confirmLabel: 'Zurückziehen',
              })
              if (ok) withdrawMutation.mutate()
            }}
            disabled={withdrawMutation.isPending}
            className="flex items-center gap-1 rounded-md bg-bg-overlay/60 border border-border-default text-text-secondary hover:text-danger hover:border-danger/40 px-3 py-1.5 text-xs font-medium transition-colors touch:opacity-100 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" /> Antrag zurückziehen
          </button>
        </div>
      )}

      {/* Requester: request cancellation of approved absence */}
      {absence && isMe && status === 'approved' && !cancellationPending && (
        <div className="px-3.5 py-2.5 border-t border-border-subtle bg-bg-overlay/20 space-y-2">
          {cancelMode ? (
            <>
              <input
                type="text"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Grund (optional)"
                disabled={requestCancelMutation.isPending}
                className="w-full text-xs rounded-md border border-border-default bg-bg-elevated px-2 py-1.5 placeholder:text-text-tertiary/60 focus:outline-none focus:ring-1 focus:ring-accent"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => requestCancelMutation.mutate(cancelReason.trim())}
                  disabled={requestCancelMutation.isPending}
                  className="flex items-center gap-1 rounded-md bg-warning/15 border border-warning/40 text-warning hover:bg-warning/25 px-3 py-1.5 text-xs font-medium transition-colors touch:opacity-100 disabled:opacity-50"
                >
                  <Check className="h-3.5 w-3.5" /> Storno beantragen
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCancelMode(false)
                    setCancelReason('')
                  }}
                  disabled={requestCancelMutation.isPending}
                  className="flex items-center gap-1 rounded-md text-text-tertiary hover:text-text-primary px-2 py-1.5 text-xs transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setCancelMode(true)}
              className="flex items-center gap-1 rounded-md bg-bg-overlay/60 border border-border-default text-text-secondary hover:text-warning hover:border-warning/40 px-3 py-1.5 text-xs font-medium transition-colors touch:opacity-100"
            >
              <X className="h-3.5 w-3.5" /> Stornierung beantragen
            </button>
          )}
        </div>
      )}

      {/* Requester: cancellation already pending → just info */}
      {absence && isMe && cancellationPending && (
        <div className="px-3.5 py-2 border-t border-border-subtle bg-warning/5 text-[11px] text-warning">
          Stornierung wurde beantragt — warte auf Genehmigung.
        </div>
      )}

      {/* Fallback when the absence row could not be loaded (e.g. caller lacks
          permission to see it, or it was hard-deleted). Suppress all action
          buttons and just show a muted info row so we don't render wrong
          state. */}
      {!absence && message.referenceId && (
        <div className="px-3.5 py-2 border-t border-border-subtle bg-bg-overlay/10 text-[11px] text-text-tertiary">
          Antragsdetails konnten nicht geladen werden.
        </div>
      )}
    </div>
  )
}

/** Shared approve/reject row used by both review flows (pending request + pending cancellation). */
function ReviewActions({
  disabled,
  note,
  setNote,
  onApprove,
  onReject,
  approveLabel = 'Genehmigen',
  rejectLabel = 'Ablehnen',
}: {
  disabled: boolean
  note: string
  setNote: (v: string) => void
  onApprove: () => void
  onReject: () => void
  approveLabel?: string
  rejectLabel?: string
}) {
  return (
    <div className="px-3.5 py-2.5 border-t border-border-subtle bg-bg-overlay/20 space-y-2">
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Notiz (optional)"
        disabled={disabled}
        className="w-full text-xs rounded-md border border-border-default bg-bg-elevated px-2 py-1.5 placeholder:text-text-tertiary/60 focus:outline-none focus:ring-1 focus:ring-accent"
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onApprove}
          disabled={disabled}
          className="flex items-center gap-1 rounded-md bg-success/15 border border-success/40 text-success px-3 py-1.5 text-xs font-medium hover:bg-success/25 transition-colors touch:opacity-100 disabled:opacity-50"
          title={approveLabel}
        >
          <Check className="h-3.5 w-3.5" /> {approveLabel}
        </button>
        <button
          type="button"
          onClick={onReject}
          disabled={disabled}
          className="flex items-center gap-1 rounded-md bg-danger/15 border border-danger/40 text-danger px-3 py-1.5 text-xs font-medium hover:bg-danger/25 transition-colors touch:opacity-100 disabled:opacity-50"
          title={rejectLabel}
        >
          <X className="h-3.5 w-3.5" /> {rejectLabel}
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Share-as-link side panel
// ============================================================================

function ShareLinkPanel({
  chatId,
  onClose,
  onShared,
}: {
  chatId: string
  onClose: () => void
  onShared: () => void
}) {
  const [tab, setTab] = useState<'project' | 'own'>('own')
  const [search, setSearch] = useState('')
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)
  const [permission, setPermission] = useState<'view' | 'edit'>('view')

  const { data: ownData, isLoading: ownLoading } = useQuery({
    queryKey: ['documents', { incoming: false }],
    queryFn: () => api.listDocuments(),
  })
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
  })

  const ownDocs = useMemo(() => ownData?.documents ?? [], [ownData])

  // Pull docs for every project the user has access to (already implicit via
  // /api/projects). Sequential fetch keeps the request count bounded.
  const { data: projectDocsData } = useQuery({
    queryKey: ['project-documents-all', projectsData?.projects.map((p) => p.id).join(',')],
    queryFn: async () => {
      const out: Array<{ project: Project; documents: DocumentSummary[] }> = []
      for (const p of projectsData?.projects ?? []) {
        try {
          const r = await api.listProjectDocuments(p.id)
          if (r.documents.length > 0) out.push({ project: p, documents: r.documents })
        } catch {
          /* skip inaccessible */
        }
      }
      return out
    },
    enabled: !!projectsData && tab === 'project',
  })

  const filteredOwn = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return ownDocs
    return ownDocs.filter((d) => d.filename.toLowerCase().includes(q))
  }, [ownDocs, search])

  const filteredProject = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return projectDocsData ?? []
    return (projectDocsData ?? [])
      .map((g) => ({
        ...g,
        documents: g.documents.filter(
          (d) => d.filename.toLowerCase().includes(q) || g.project.name.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.documents.length > 0)
  }, [projectDocsData, search])

  const share = useMutation({
    mutationFn: () =>
      api.shareDocumentAsChatLink(chatId, {
        documentId: selectedDocId ?? '',
        permission,
      }),
    onSuccess: () => {
      notify.success('Datei geteilt')
      onShared()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Datei als Link teilen"
      description="Empfänger erhält Zugriff auf die ausgewählte Datei. Eine echte Kopie wird nicht erstellt."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button
            type="button"
            disabled={!selectedDocId || share.isPending}
            onClick={() => share.mutate()}
          >
            Senden
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        <div className="flex rounded-lg border border-border-subtle bg-bg-overlay/30 p-0.5">
          <button
            type="button"
            className={cn(
              'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              tab === 'own'
                ? 'bg-bg-elevated text-text-primary shadow-sm'
                : 'text-text-tertiary hover:text-text-primary',
            )}
            onClick={() => setTab('own')}
          >
            Eigene Dateien
          </button>
          <button
            type="button"
            className={cn(
              'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              tab === 'project'
                ? 'bg-bg-elevated text-text-primary shadow-sm'
                : 'text-text-tertiary hover:text-text-primary',
            )}
            onClick={() => setTab('project')}
          >
            Projektdateien
          </button>
        </div>

        <div className="space-y-2">
          <Label htmlFor="share-search">Suchen</Label>
          <Input
            id="share-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Dateiname oder Projekt …"
          />
        </div>

        <div className="space-y-2">
          <Label>Berechtigung</Label>
          <Select
            value={permission}
            onChange={(e) => setPermission(e.target.value as 'view' | 'edit')}
          >
            <option value="view">Ansehen</option>
            <option value="edit">Bearbeiten</option>
          </Select>
        </div>

        {tab === 'own' ? (
          <div className="rounded-lg border border-border-subtle divide-y divide-border-subtle max-h-72 overflow-y-auto">
            {ownLoading ? (
              <p className="p-3 text-sm text-text-tertiary">Lade …</p>
            ) : filteredOwn.length === 0 ? (
              <p className="p-3 text-sm text-text-tertiary">Keine Dateien gefunden.</p>
            ) : (
              filteredOwn.map((d) => (
                <DocPickRow
                  key={d.id}
                  doc={d}
                  selected={selectedDocId === d.id}
                  onClick={() => setSelectedDocId(d.id)}
                />
              ))
            )}
          </div>
        ) : (
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {filteredProject.length === 0 ? (
              <p className="p-3 text-sm text-text-tertiary">Keine Projektdateien gefunden.</p>
            ) : (
              filteredProject.map((g) => (
                <div key={g.project.id} className="rounded-lg border border-border-subtle">
                  <header className="px-3 py-1.5 bg-bg-overlay/40 border-b border-border-subtle">
                    <p className="text-xs font-medium text-text-secondary">{g.project.name}</p>
                  </header>
                  <ul className="divide-y divide-border-subtle">
                    {g.documents.map((d) => (
                      <DocPickRow
                        key={d.id}
                        doc={d}
                        selected={selectedDocId === d.id}
                        onClick={() => setSelectedDocId(d.id)}
                      />
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </SidePanel>
  )
}

function DocPickRow({
  doc,
  selected,
  onClick,
}: {
  doc: DocumentSummary
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-bg-overlay/40 transition-colors',
        selected && 'bg-accent/10',
      )}
    >
      <FileText className="h-4 w-4 text-text-tertiary shrink-0" strokeWidth={1.5} />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-text-primary truncate">{doc.filename}</p>
        <p className="text-2xs text-text-tertiary tabular-nums">
          {formatBytes(doc.sizeBytes)} · {new Date(doc.createdAt).toLocaleDateString('de-DE')}
        </p>
      </div>
      {selected && <CheckCircle2 className="h-4 w-4 text-accent shrink-0" />}
    </button>
  )
}

// ============================================================================
// Absence quick-action panel (slim wrapper around the absence endpoint)
// ============================================================================

function ChatAbsencePanel({
  chatId,
  recipientName,
  onClose,
  onSubmitted,
}: {
  chatId: string
  recipientName: string
  onClose: () => void
  onSubmitted: () => void
}) {
  const [type, setType] = useState<AbsenceType>('vacation')
  const [fromDate, setFromDate] = useState(todayIso())
  const [toDate, setToDate] = useState(todayIso())
  const [firstHalf, setFirstHalf] = useState<AbsenceHalf | ''>('')
  const [lastHalf, setLastHalf] = useState<AbsenceHalf | ''>('')
  const [notes, setNotes] = useState('')

  const sameDay = fromDate === toDate

  const submit = useMutation({
    mutationFn: () =>
      api.requestAbsenceViaChat(chatId, {
        type,
        fromDate,
        toDate,
        firstDayHalf: firstHalf || null,
        lastDayHalf: sameDay ? null : lastHalf || null,
        notes: notes || null,
      }),
    onSuccess: () => {
      notify.success('Antrag gesendet')
      onSubmitted()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Abwesenheit beantragen"
      description={
        recipientName
          ? `Geht direkt an ${recipientName} zur Genehmigung.`
          : 'Geht direkt an deinen Vorgesetzten zur Genehmigung.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button type="button" disabled={submit.isPending} onClick={() => submit.mutate()}>
            Antrag senden
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="ca-type">Typ</Label>
          <Select
            id="ca-type"
            value={type}
            onChange={(e) => setType(e.target.value as AbsenceType)}
          >
            <option value="vacation">Urlaub</option>
            <option value="sick">Krank</option>
            <option value="training">Fortbildung</option>
            <option value="other">Sonstiges</option>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="ca-from">Von</Label>
            <Input
              id="ca-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ca-to">Bis</Label>
            <Input
              id="ca-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>
        {sameDay ? (
          <div className="space-y-2">
            <Label htmlFor="ca-firsthalf">Tageslänge</Label>
            <Select
              id="ca-firsthalf"
              value={firstHalf}
              onChange={(e) => setFirstHalf((e.target.value as AbsenceHalf) || '')}
            >
              <option value="">Ganzer Tag</option>
              <option value="morning">Nur vormittags</option>
              <option value="afternoon">Nur nachmittags</option>
            </Select>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="ca-firsthalf">Erster Tag</Label>
              <Select
                id="ca-firsthalf"
                value={firstHalf}
                onChange={(e) => setFirstHalf((e.target.value as AbsenceHalf) || '')}
              >
                <option value="">Ganz</option>
                <option value="afternoon">Nur nachmittags</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ca-lasthalf">Letzter Tag</Label>
              <Select
                id="ca-lasthalf"
                value={lastHalf}
                onChange={(e) => setLastHalf((e.target.value as AbsenceHalf) || '')}
              >
                <option value="">Ganz</option>
                <option value="morning">Nur vormittags</option>
              </Select>
            </div>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="ca-notes">Notiz (optional)</Label>
          <Input
            id="ca-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="z.B. Familienurlaub"
          />
        </div>
      </div>
    </SidePanel>
  )
}

// ============================================================================
// New chat dialog
// ============================================================================

// ============================================================================
// Helpers
// ============================================================================

function chatTitle(chat: ChatListItem, myId: string): string {
  if (chat.isGroup) return chat.name ?? 'Gruppe'
  const other = chat.members.find((m) => m.id !== myId)
  return other?.name ?? '1-zu-1'
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function formatRelative(d: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'jetzt'
  if (diffMin < 60) return `${diffMin}m`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `${diffH}h`
  const sameYear = d.getFullYear() === now.getFullYear()
  return d.toLocaleDateString(
    'de-DE',
    sameYear
      ? { day: '2-digit', month: '2-digit' }
      : { day: '2-digit', month: '2-digit', year: '2-digit' },
  )
}
